// Shared config + helpers for the "Alterable / Customise Fit" feature.

// Fixed preset of measurements available when a product is alterable.
export const MEASUREMENTS = [
  { key: "bust", label: "Bust" },
  { key: "waist", label: "Waist" },
  { key: "hips", label: "Hips" },
];

export const MEASUREMENT_LABEL = MEASUREMENTS.reduce((m, x) => ((m[x.key] = x.label), m), {});

// Standard garment measurements (inches) per size — from the store size guide.
export const SIZE_CHART = {
  S:   { bust: 34, waist: 27, hips: 37 },
  M:   { bust: 37, waist: 30, hips: 40 },
  L:   { bust: 40, waist: 33, hips: 43 },
  XL:  { bust: 42, waist: 35, hips: 45 },
  XXL: { bust: 44, waist: 37, hips: 47 },
  XXXL: { bust: 46, waist: 39, hips: 49 },
};

// Standard measurement for a given size + measurement key.
// Falls back to the M row for non-chart (custom) sizes.
export const standardFor = (size, key) => {
  const row = SIZE_CHART[size] || SIZE_CHART.M;
  return row ? row[key] : undefined;
};

// Adjustment options in whole-inch steps: from -down (smaller) to +up (larger).
export const adjustmentOptions = (up, down) => {
  const hi = Math.floor(Number(up) || 0);
  const lo = -Math.floor(Number(down) || 0);
  const opts = [];
  for (let v = lo; v <= hi; v++) opts.push(v);
  return opts;
};

export const fmtIn = (n) => `${Math.round(Number(n) * 10) / 10}"`;
export const fmtAdj = (n) => (n > 0 ? `+${n}"` : n < 0 ? `${n}"` : "Standard");

// Which way a garment can be altered. Defaults to both (up or down).
export const alterationDirection = (product) => {
  const d = product?.alteration_direction;
  return d === "smaller" || d === "larger" ? d : "both";
};

// How much larger (up) and smaller (down) a garment can be altered, in inches.
// Prefers the explicit per-direction values; falls back to the legacy single
// allowance + direction for older products.
export const alterationBounds = (product) => {
  let up = Number(product?.alteration_up) || 0;
  let down = Number(product?.alteration_down) || 0;
  if (up === 0 && down === 0) {
    const allow = Number(product?.alteration_allowance) || 0;
    const dir = alterationDirection(product);
    up = dir === "smaller" ? 0 : allow;
    down = dir === "larger" ? 0 : allow;
  }
  return { up: Math.floor(up), down: Math.floor(down) };
};

// Measurements available for a product at a given size, each with its own
// larger (up) / smaller (down) allowance.
export const availableMeasurements = (product, size) => {
  const { up, down } = alterationBounds(product);
  if (up <= 0 && down <= 0) return [];
  return MEASUREMENTS.filter((m) => standardFor(size, m.key) != null)
    .map((m) => ({ ...m, up, down, standard: standardFor(size, m.key) }));
};

export const isAlterable = (product) => {
  if (!product?.alterable) return false;
  const { up, down } = alterationBounds(product);
  return up > 0 || down > 0;
};

export const CONFIRM_TEXT =
  "I confirm that I have reviewed my selected measurements and understand that this garment will be tailored according to my selections. Customised garments cannot be changed once production has begun.";

// Extra charge per customised garment (must match backend CUSTOM_FIT_FEE).
export const CUSTOM_FIT_FEE = 10;
